import React from 'react';
import styled, { keyframes } from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import Placeholder from '@component/common/placeholder';
import Line from '@component/common/line';
import ImageAudioButton from '@design-system/image-audio-button';
// import IconButton from '@design-system/icon-button';
import IconBlock from '@component/common/block/icon-block';
import Quote from '@design-system/quote';
import Canvas from '@design-system/canvas';
import { TWorksheetStudyContent, IClozeExplanation } from '@datatype/iyagigo/api-type';
import { ETheme } from '@datatype/type';
import { completeSentence, replaceDashWithTilde } from '@util/string';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  translationVisible: boolean;
  handleReveal: () => void;
  title: string;
  description: string;
  source: TWorksheetStudyContent;
  sentence: string;
  vocabularyItems: (IClozeExplanation & { id: string })[];
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const pointerFloat = keyframes`
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-2px);
    }
`;
const MeaningButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: ${size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT};
  cursor: pointer;
`;
const PointerContainer = styled.span`
  display: flex;
  animation: ${pointerFloat} 1.5s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
const SentenceControlContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: ${size.MARGIN_1};
`;
const SentenceKeywordItemsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
`;
const SentenceContent: React.FC<IProps> = ({
  title,
  description,
  source,
  sentence,
  vocabularyItems,
  translationVisible,
  handleReveal,
}) => {
  const isEnglish = source.languageCode === 'EN';
  const pronunciation = isEnglish ? '' : source.sentencePronunciationJa;
  const audioLink = isEnglish ? source.sentenceAudioLinkEn : source.sentenceAudioLinkJa;
  const SentenceKeywordItems = vocabularyItems.map((vocabulary) => (
    <Quote key={vocabulary.id} width='100%'>
      <TextBlock
        text={vocabulary.keyword}
        design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
      />
      <TextBlock
        text={vocabulary.meaning.join(', ')}
        design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
      />
    </Quote>
  ));
  return (
    <Wrapper>
      <TextBlock
        text={title}
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      {/*<Gap height={size.MARGIN_1} />*/}
      <TextBlock
        text={description}
        design={{ text: { size: 'medium', color: color.BLACK_4 }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_2} />
      <Canvas
        theme={ETheme.PAPER}
        shape='round'
        width='100%'
        padding={size.CONTENT_PADDING}
        shadow={true}
        alignX='center'>
        <TextBlock
          text={sentence}
          annotation={pronunciation ? { reading: pronunciation } : undefined}
          design={{
            text: {
              size: 'large',
              color: color.BLACK_2,
              bold: false,
            },
            box: { alignX: 'left', width: '100%' },
          }}
        />
        <Gap height={size.MARGIN_1} />
        <Placeholder
          visible={translationVisible}
          placeholder={
            <MeaningButtonContainer onClick={handleReveal}>
              <PointerContainer>
                <IconBlock icon='touch-app' design={{ icon: { size: 'small', color: color.GRAY_1 } }} />
              </PointerContainer>
              <TextBlock text='뜻 보기' design={{ text: { size: 'medium', color: color.GRAY_1 } }} />
            </MeaningButtonContainer>
          }>
          <TextBlock
            text={completeSentence(source.sentenceTemplateKo, source.answerKo, false) || source.sentenceKo}
            design={{ text: { size: 'medium', color: color.BROWN_1 }, box: { alignX: 'left', width: '100%' } }}
          />
        </Placeholder>
        {/*<Gap height={size.MARGIN_1} />*/}
        <Line color={color.GRAY_2} margin={`${size.MARGIN_1} 0`} />
        <SentenceControlContainer>
          {audioLink && (
            <ImageAudioButton
              sources={[audioLink]}
              theme={ETheme.GRAY}
              tone='normal'
              kind='outlined'
              size='small'
              shape='round'
              width='45px'
            />
          )}
          {/* 기존 번역 토글
          <IconButton
            icon={translationVisible ? 'language' : 'language-off'}
            theme={ETheme.GRAY}
            tone='normal'
            kind='outlined'
            size='small'
            shape='round'
            width='45px'
            handleClick={handleReveal}
          />
          */}
        </SentenceControlContainer>
      </Canvas>
      {translationVisible && (
        <>
          {source.answerExplanationKo && (
            <>
              <Gap height={size.MARGIN_2} />
              <TextBlock
                text='문형'
                design={{
                  text: { size: 'large', color: color.BLACK_2, bold: true },
                  box: { alignX: 'left' },
                }}
              />
              <Gap height={'5px'} />
              <Quote width='100%'>
                <TextBlock
                  text={replaceDashWithTilde(source.answerExplanationKo.keyword)}
                  design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
                />
                <TextBlock
                  text={replaceDashWithTilde(source.answerExplanationKo.meaning?.join(', ') ?? '')}
                  design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
                />
              </Quote>
              <Gap height={size.MARGIN_1} />
            </>
          )}
          <>
            <Gap height={size.MARGIN_1} />
            <TextBlock
              text='어휘'
              design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
            />
            <Gap height={'5px'} />
            <SentenceKeywordItemsContainer>{SentenceKeywordItems}</SentenceKeywordItemsContainer>
          </>
        </>
      )}
    </Wrapper>
  );
};

export default SentenceContent;
